import { classOffersAnyEnrollment, type ClassOfferRow } from '@/utils/programs/classOffersEnrollmentType';

/**
 * Public enrollment gating predicate — the ONE place that decides whether a
 * public program reads as "Waitlist Only", "Enrollment Closed", or (PR 1 of
 * #4724) "Sign-ups open <date>".
 *
 * Split the case, don't remove the gate:
 * - Open gyms: capacity is per session, so the lifetime `spots_remaining`
 *   number (which the public API now sends as `null` for open gyms) must NEVER
 *   flip them to waitlist. Only explicit staff intent gates them —
 *   `enrollment_status = 'waitlist_only'` or `'closed'`.
 * - Regular classes: unchanged — `spots_remaining === 0` still means waitlist
 *   (when the status doesn't already say so). A null/undefined spots value is
 *   "no signal", matching the existing typeof-number badge guards.
 * - First class date (#4724): the public API sends `starts_on` only while an
 *   ongoing class's first meeting is still ahead of gym-local today. It is a
 *   label ("Starts <date>"), not a gate — families enroll before it and the
 *   checkout decides what (if anything) is charged today.
 *
 * - A class that sells nothing (#5302): a regular class with monthly and
 *   drop-ins both off reads as closed, because the enroll route refuses every
 *   type on it. Callers that have the class's offer flags pass `classOffer`.
 *
 * Used by the program detail page (`isWaitlistOnly`) and the catalog's
 * availability filter bucketing + card badge, so both surfaces classify
 * identically.
 */

export interface PublicEnrollmentGatingInput {
  /** program_type === 'class' && is_open_gym === true */
  isOpenGym: boolean;
  /** 'open' | 'closed' | 'waitlist_only' */
  enrollmentStatus: string | null | undefined;
  /** Lifetime spots number; null for open gyms at the public read boundary */
  spotsRemaining: number | null | undefined;
  /** The class's offer flags. Omit for anything that is not a class. */
  classOffer?: ClassOfferRow | null;
}

export interface PublicEnrollmentGating {
  /** True when the program should present "Waitlist Only" / Join Waitlist */
  waitlistOnly: boolean;
  /** True when staff explicitly closed enrollment, or the class sells nothing */
  closed: boolean;
}

export function getPublicEnrollmentGating(
  input: PublicEnrollmentGatingInput
): PublicEnrollmentGating {
  const sellsNothing =
    !input.isOpenGym && input.classOffer != null && !classOffersAnyEnrollment(input.classOffer);
  const closed = input.enrollmentStatus === 'closed' || sellsNothing;
  const explicitWaitlist = input.enrollmentStatus === 'waitlist_only';
  if (input.isOpenGym) {
    // Staff intent only — lifetime spots never gate an open gym.
    return { waitlistOnly: explicitWaitlist, closed };
  }

  return {
    waitlistOnly: explicitWaitlist || input.spotsRemaining === 0,
    closed,
  };
}
